import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Users, Plus, Sparkles } from "lucide-react";

export default function AnonRoomsPanel({ open, onClose, onJoined }) {
  const [rooms, setRooms] = useState([]);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/anon-rooms");
      setRooms(data);
    } catch {}
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const create = async () => {
    setError("");
    if (name.trim().length < 2) return setError("İsim çok kısa");
    setCreating(true);
    try {
      const { data } = await api.post("/anon-rooms", { name: name.trim(), topic: topic.trim() });
      setName("");
      setTopic("");
      setRooms((r) => [data, ...r]);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const join = async (room) => {
    try {
      const { data } = await api.post(`/anon-rooms/${room.id}/join`);
      onJoined?.(room, data);
      onClose?.();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="anon-rooms-panel">
      <div className="bg-white border-hard shadow-hard-lg rounded-2xl w-full max-w-xl p-6 slide-up max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-3xl flex items-center gap-2"><Sparkles strokeWidth={3} className="w-6 h-6" /> Anonim odalar</h2>
          <button onClick={onClose} data-testid="anon-close-btn" className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn">
            <X strokeWidth={3} className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-[#555] mb-4">Katılınca sana rastgele bir maske ismi verilir (ör. <b>Mor Kedi #42</b>). Gerçek kimliğin gizli.</p>

        <div className="bg-[#F9F8F6] border-hard-2 rounded-xl p-3 mb-4">
          <div className="text-xs uppercase font-black tracking-widest mb-2">Yeni oda oluştur</div>
          <div className="flex flex-col md:flex-row gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="anon-name-input" placeholder="Oda adı" className="flex-1 bg-white border-hard-2 rounded-lg px-3 py-2 text-sm" />
            <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="anon-topic-input" placeholder="Konu (opsiyonel)" className="flex-1 bg-white border-hard-2 rounded-lg px-3 py-2 text-sm" />
            <button onClick={create} disabled={creating} data-testid="anon-create-btn" className="bg-[#CCFF00] border-hard shadow-hard-sm rounded-lg px-4 py-2 font-black press-btn flex items-center gap-1 disabled:opacity-60">
              <Plus strokeWidth={3} className="w-4 h-4" /> Oluştur
            </button>
          </div>
          {error && <div className="mt-2 text-sm font-bold bg-[#FF9E80] border-hard-2 rounded-lg px-2 py-1" data-testid="anon-error">{error}</div>}
        </div>

        <div className="divide-y-2 divide-[#0A0A0A] border-hard-2 rounded-xl overflow-hidden">
          {rooms.length === 0 && <div className="p-6 text-center text-sm font-semibold text-[#555]">Henüz oda yok — ilkini sen oluştur.</div>}
          {rooms.map((r) => (
            <button
              key={r.id}
              onClick={() => join(r)}
              data-testid={`anon-room-${r.id}`}
              className="w-full flex items-center gap-3 p-4 hover:bg-[#F9F8F6] text-left"
            >
              <div className="w-11 h-11 rounded-xl bg-[#D4B3FF] border-hard-2 flex items-center justify-center">
                <Users strokeWidth={3} className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-black">{r.name}</div>
                {r.topic && <div className="text-xs text-[#555]">{r.topic}</div>}
              </div>
              <div className="text-[11px] font-black uppercase tracking-widest bg-[#CCFF00] border-hard-2 rounded-full px-2 py-0.5 shrink-0 whitespace-nowrap">
                {r.member_count || 0} maske
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
